import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Scale } from "lucide-react";
import { AeroNav } from "@/components/aero/AeroNav";
import { AeroFooter } from "@/components/aero/AeroFooter";
import { POLICIES, POLICY_LIST } from "@/constants/policies";

const ease = [0.16, 1, 0.3, 1];

export default function PolicyPage() {
  const { slug } = useParams();
  const policy = POLICIES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-[#F8F6F2] text-[#111827] min-h-screen"
      data-testid="policy-page"
    >
      <AeroNav />
      <section className="max-w-[900px] mx-auto px-6 md:px-10 pt-36 pb-24 md:pt-44">
        {!policy ? (
          <div className="text-center py-20" data-testid="policy-not-found">
            <h1 className="font-serif text-4xl text-[#111827]">Page not found</h1>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] font-bold tracking-[0.22em] uppercase text-[#0F2A43] hover:text-[#4F6D8A] transition-colors">
              <ArrowLeft size={13} /> Back to Home
            </Link>
          </div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }}>
              <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#4F6D8A] flex items-center gap-3">
                <Scale size={14} /> Legal
              </p>
              <h1 className="mt-4 font-serif text-[36px] md:text-[52px] leading-[1.05] text-[#111827]" data-testid="policy-title">{policy.title}</h1>
              <p className="mt-3 font-mono text-[9px] tracking-[0.25em] uppercase text-[#9CA3AF]">Last updated: {policy.updated}</p>
              <p className="mt-6 text-[#374151] leading-relaxed">{policy.intro}</p>
              <span className="mt-8 block w-14 h-[2px] bg-[#4F6D8A]" />
            </motion.div>

            <div className="mt-12 space-y-4">
              {policy.sections.map((s, i) => (
                <motion.div
                  key={s.h}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.2), ease }}
                  data-testid={`policy-section-${i}`}
                  className="bg-white rounded-[18px] border border-[#E5E7EB] p-7 shadow-[0_10px_30px_-15px_rgba(15,42,67,0.12)]"
                >
                  <h2 className="font-display font-bold text-lg text-[#0F2A43]">{s.h}</h2>
                  <p className="mt-3 text-sm text-[#4B5563] leading-[1.85]">{s.p}</p>
                </motion.div>
              ))}
            </div>

            {/* other policies */}
            <div className="mt-14 border-t border-[#E5E7EB] pt-8">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6B7280]">More Policies</p>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2.5">
                {POLICY_LIST.filter((p) => p.slug !== slug).map((p) => (
                  <Link
                    key={p.slug}
                    to={`/policies/${p.slug}`}
                    data-testid={`policy-link-${p.slug}`}
                    className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#4F6D8A] hover:text-[#0F2A43] transition-colors underline underline-offset-4 decoration-[#E5E7EB] hover:decoration-[#0F2A43]"
                  >
                    {p.label}
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </section>
      <AeroFooter />
    </motion.main>
  );
}
